import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  Plus,
  Trash2,
} from 'lucide-react';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';

// ── Category colour mapping ──────────────────────────────────────────────────
const categoryTone = {
  Venue: 'bg-stone-100 text-stone-700',
  Guests: 'bg-sky-100 text-sky-700',
  Vendors: 'bg-violet-100 text-violet-700',
  Budget: 'bg-emerald-100 text-emerald-700',
  Attire: 'bg-fuchsia-100 text-fuchsia-700',
  Travel: 'bg-cyan-100 text-cyan-700',
  Logistics: 'bg-amber-100 text-amber-700',
  Ceremony: 'bg-rose-100 text-rose-700',
  Entertainment: 'bg-indigo-100 text-indigo-700',
  Jewelry: 'bg-yellow-100 text-yellow-700',
  Design: 'bg-pink-100 text-pink-700',
  Photos: 'bg-purple-100 text-purple-700',
  Events: 'bg-orange-100 text-orange-700',
  Planning: 'bg-slate-100 text-slate-700',
};

// ── Owner colour mapping ─────────────────────────────────────────────────────
const ownerTone = {
  Both: 'bg-amber-100 text-amber-700 ring-amber-200',
  Kate: 'bg-rose-100 text-rose-700 ring-rose-200',
  Nav: 'bg-sky-100 text-sky-700 ring-sky-200',
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseDateLocal = (str) => {
  if (!str) return null;
  const d = new Date(`${str}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const diffDays = (dateStr) => {
  const target = parseDateLocal(dateStr);
  if (!target) return null;
  const now = today();
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDate = (str) => {
  const d = parseDateLocal(str);
  if (!d) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Main component ───────────────────────────────────────────────────────────
const WeddingToDo = ({ board, fetchBoard, onEditTask }) => {
  // To-Do is the working list: default to what is still outstanding rather
  // than to a wall that is mostly completed history.
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [categoryFilter, setCategoryFilter] = useState(null);

  const deletedRef = useRef(null);

  // ── Flatten + sort tasks ──────────────────────────────────────────────────
  const allTasks = useMemo(() => {
    const tasks = (board?.months ?? []).flatMap((m) => m.tasks ?? []);
    return tasks.sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    });
  }, [board]);

  // ── Auto-generate category pills ─────────────────────────────────────────
  const categories = useMemo(
    () => [...new Set(allTasks.map((t) => t.category).filter(Boolean))],
    [allTasks]
  );

  // ── Apply filters ─────────────────────────────────────────────────────────
  const filteredTasks = useMemo(() => {
    return allTasks.filter((t) => {
      // "Pending" means "still outstanding", which includes in-progress work.
      // Only `completed` counts as done -- see TASK_STATUSES in
      // wedding_planner.py. Anything not completed belongs in the working list.
      const statusMatch =
        statusFilter === 'All' ||
        (statusFilter === 'Done' && t.status === 'completed') ||
        (statusFilter === 'Pending' && t.status !== 'completed');
      const categoryMatch = !categoryFilter || t.category === categoryFilter;
      return statusMatch && categoryMatch;
    });
  }, [allTasks, statusFilter, categoryFilter]);

  // ── Toggle status ─────────────────────────────────────────────────────────
  const toggleStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      const res = await fetch(`${ENDPOINTS.WEDDING_TASKS}/${task.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...task, status: newStatus }),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Update failed');
      await fetchBoard();
    } catch {
      toast.error('Could not update task.');
    }
  };

  // ── Delete with undo ──────────────────────────────────────────────────────
  const handleDelete = async (task) => {
    try {
      const res = await fetch(`${ENDPOINTS.WEDDING_TASKS}/${task.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Delete failed');
      deletedRef.current = { ...task };
      await fetchBoard();

      toast(
        (t) => (
          <span className="flex items-center gap-3 text-sm">
            <span>Task deleted.</span>
            <button
              className="rounded-md bg-stone-900 px-3 py-1 text-xs font-semibold text-white"
              onClick={() => {
                toast.dismiss(t.id);
                handleUndo();
              }}
            >
              Undo
            </button>
          </span>
        ),
        { duration: 5000 }
      );

      setTimeout(() => {
        deletedRef.current = null;
      }, 5500);
    } catch {
      toast.error('Could not delete task.');
    }
  };

  const handleUndo = async () => {
    const task = deletedRef.current;
    if (!task) return;
    try {
      const { id, position, ...body } = task; // eslint-disable-line no-unused-vars
      const res = await fetch(ENDPOINTS.WEDDING_TASKS, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (handleAuthError(res)) return;
      if (!res.ok) throw new Error('Undo failed');
      deletedRef.current = null;
      await fetchBoard();
      toast.success('Task restored.');
    } catch {
      toast.error('Could not undo deletion.');
    }
  };

  // ── Urgency badge ─────────────────────────────────────────────────────────
  const UrgencyBadge = ({ task }) => {
    if (task.status === 'completed' || !task.due_date) return null;
    const days = diffDays(task.due_date);
    if (days === null) return null;
    if (days < 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-red-700">
          <AlertTriangle className="h-3 w-3" /> OVERDUE
        </span>
      );
    }
    if (days <= 7) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-red-700">
          <AlertTriangle className="h-3 w-3" /> URGENT — {days}d!
        </span>
      );
    }
    if (days <= 14) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
          {days} days away
        </span>
      );
    }
    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="rounded-[30px] bg-white/80 p-4 shadow-sm ring-1 ring-stone-200 backdrop-blur sm:p-6">

      {/* ── Controls ──
          Two compact rows: status + Add on one line, categories on a single
          horizontally scrolling line. Wrapping every 44px pill pushed the first
          task most of a screen down on mobile. */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          {/* Which filter is on was previously carried by fill colour alone:
              a screen reader heard three identical buttons, and so did anyone
              who cannot separate near-black from white. `aria-pressed` states
              it outright, and the group gives the row a name. */}
          <div
            role="group"
            aria-label="Filter tasks by status"
            className="flex min-w-0 gap-2 overflow-x-auto"
          >
            {['All', 'Pending', 'Done'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                aria-pressed={statusFilter === s}
                className={clsx(
                  'inline-flex min-h-[44px] shrink-0 items-center rounded-full px-3.5 py-2 text-xs font-semibold transition',
                  statusFilter === s
                    ? 'bg-stone-900 text-white'
                    : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                )}
              >
                {s}
              </button>
            ))}
          </div>

          <button
            onClick={() => onEditTask?.(null)}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-stone-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-stone-800"
          >
            <Plus className="h-4 w-4" />
            Add Task
          </button>
        </div>

        {categories.length > 0 && (
          <div
            role="group"
            aria-label="Filter tasks by category"
            className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
          >
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
                aria-pressed={categoryFilter === cat}
                className={clsx(
                  'inline-flex min-h-[44px] shrink-0 items-center rounded-full px-3.5 py-2 text-xs font-semibold transition',
                  categoryFilter === cat
                    ? 'bg-stone-900 text-white'
                    : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Task list ──
          `board` only ever arrives here from a successful response (the
          container renders an error panel instead when the fetch failed), so
          these empty states genuinely mean "there is nothing", not "we could
          not read it". The two cases are still worth separating: an empty
          dataset is different from filters that exclude everything. */}
      {allTasks.length === 0 ? (
        <div className="rounded-2xl bg-stone-50 py-16 text-center text-stone-500">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 opacity-30" />
          <p className="text-sm font-medium">No tasks yet.</p>
          <p className="mt-1 text-xs">Add your first task to start planning.</p>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="rounded-2xl bg-stone-50 py-16 text-center text-stone-500">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 opacity-30" />
          <p className="text-sm font-medium">
            No tasks match these filters.
          </p>
          <p className="mt-1 text-xs">
            {allTasks.length} task{allTasks.length === 1 ? '' : 's'} hidden by the current filters.
          </p>
          <button
            type="button"
            onClick={() => { setStatusFilter('All'); setCategoryFilter(null); }}
            className="mt-4 inline-flex min-h-[44px] items-center rounded-full border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-700 transition hover:bg-stone-100"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {filteredTasks.map((task) => {
            const done = task.status === 'completed';
            const inProgress = task.status === 'in_progress';
            const catClass = categoryTone[task.category] ?? 'bg-stone-100 text-stone-600';
            const ownerClass =
              ownerTone[task.owner] ?? 'bg-stone-100 text-stone-600 ring-stone-200';

            return (
              <li
                key={task.id}
                className={clsx(
                  'flex items-start gap-3 rounded-2xl border p-4 transition',
                  done
                    ? 'border-stone-100 bg-stone-50'
                    : 'border-stone-100 bg-white shadow-sm hover:shadow-md'
                )}
              >
                {/* Circular checkbox.
                    The tick stays 24px; only the hit area grows to 44x44, and
                    the negative margins spend that growth on the row's own
                    padding and gap rather than on the layout. Net cost to the
                    list: 8px of horizontal space and nothing vertical. */}
                <button
                  type="button"
                  onClick={() => toggleStatus(task)}
                  className="-my-2 -ml-2 -mr-1 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                  aria-label={done ? 'Mark as pending' : 'Mark as completed'}
                  aria-pressed={done}
                >
                  <CheckCircle2
                    className={clsx(
                      'h-6 w-6 transition',
                      done ? 'text-emerald-600' : 'text-stone-500 hover:text-emerald-600'
                    )}
                    strokeWidth={done ? 2.5 : 1.5}
                    aria-hidden="true"
                  />
                </button>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  {/* Title row */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* The title is how you open a task, and a single-line
                        title gave that a 19px-tall target. `py-3` with a
                        matching `-my-3` buys the 44px without moving the text
                        or growing the row: min-height counts the padding, and
                        the negative margin gives the layout its 19px back. */}
                    <button
                      type="button"
                      onClick={() => onEditTask?.(task)}
                      className={clsx(
                        '-my-3 min-h-[44px] py-3 text-left text-sm font-semibold leading-snug focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400',
                        done ? 'text-stone-500 line-through' : 'text-stone-900 hover:text-stone-600'
                      )}
                    >
                      {task.title}
                    </button>

                    {inProgress && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                        <CircleDashed className="h-3 w-3" />
                        In progress
                      </span>
                    )}

                    {task.is_milestone && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                        Milestone
                      </span>
                    )}

                    <UrgencyBadge task={task} />
                  </div>

                  {/* Pills row */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {task.category && (
                      <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', catClass)}>
                        {task.category}
                      </span>
                    )}
                    {task.owner && (
                      <span
                        className={clsx(
                          'rounded-full px-2 py-0.5 text-[11px] font-medium ring-1',
                          ownerClass
                        )}
                      >
                        {task.owner}
                      </span>
                    )}
                    {task.due_date && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-stone-500">
                        <CalendarDays className="h-3 w-3" />
                        {formatDate(task.due_date)}
                      </span>
                    )}
                  </div>

                  {/* Verbose completion / audit notes live behind a disclosure
                      so the working list stays scannable. */}
                  {task.notes && (
                    <details className="mt-1.5">
                      {/* Same trick as the tick and the bin: a 44px-tall
                          target whose extra height is taken out of the row's
                          own padding rather than added to the row. */}
                      <summary className="-mt-1 -mb-2 inline-flex min-h-[44px] cursor-pointer list-none items-center rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-600 hover:text-stone-800">
                        Details
                      </summary>
                      <p className="mt-1.5 rounded-xl bg-stone-50 px-3 py-2 text-xs leading-5 text-stone-500 ring-1 ring-stone-200">
                        {task.notes}
                      </p>
                    </details>
                  )}
                </div>

                {/* Delete. Destructive and previously a 16px target — the
                    easiest control on the page to hit by accident and the
                    hardest to hit on purpose. Same trick as the tick: 44x44
                    hit area, unchanged 16px icon. */}
                <button
                  type="button"
                  onClick={() => handleDelete(task)}
                  className="-my-2 -mr-2 -ml-1 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full text-stone-500 transition hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                  aria-label={`Delete task: ${task.title}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

    </div>
  );
};

export default WeddingToDo;
